<template>
    <v-form ref="form" v-model="isFormValid">
        <v-col cols="12" lg="8">
            <b-alert
                v-if="rolesLoaded && _.isEmpty(roles)"
                type="warning"
                :message="t('users::create.warning.no-roles')"
            >
                <v-btn
                    v-if="can('roles.create')"
                    :href="$root.route('roles.create')"
                    link
                    small
                    style="float: right"
                    text
                >
                    {{ t('users::create.warning.create-roles') }}
                </v-btn>
            </b-alert>
        </v-col>

        <v-col cols="12" class="tabs">
            <v-tabs v-model="tabKey" show-arrows>
                <v-tab>
                    <div class="tab">
                        <v-icon left>mdi-account</v-icon>
                        {{ t('users::form.general_section') }}
                    </div>
                </v-tab>
                <v-tab>
                    <div class="tab">
                        <v-icon left>mdi-account-multiple</v-icon>
                        {{ t('users::form.permissions') }}
                    </div>
                </v-tab>
                <v-tab-item>
                    <div class="pt-10 px-2">
                        <v-row>
                            <v-col cols="12" lg="3">
                                <b-image
                                    v-if="user.avatar"
                                    :src="user.avatar"
                                    bordered
                                    deletable
                                    @delete="removeAvatar"
                                ></b-image>
                                <v-img v-if="!user.avatar" class="bordered" src="/images/no-image.jpg"></v-img>
                                <v-file-input
                                    v-model="avatar"
                                    :error-messages="errorMessages.avatar_base64"
                                    accept="image/png, image/jpeg, image/bmp"
                                    chips
                                    counter
                                    rounded
                                    dense
                                    background-color="#fff"
                                    label="Upload photo"
                                    outlined
                                    prepend-icon="mdi-camera"
                                    show-size
                                ></v-file-input>
                            </v-col>
                            <v-col cols="12" lg="9">
                                <v-row>
                                    <v-col cls="12" lg="6" class="py-0">
                                        <v-text-field
                                            v-model="user.email"
                                            :error-messages="errorMessages.email"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t('users::create.email')}`"
                                            :rules="[
                                                (v) => !!v || t('default::validation.field.required'),
                                                $root.rules.email,
                                            ]"
                                            dense
                                            background-color="#fff"
                                            outlined
                                            rounded
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cls="12" lg="6" class="py-0 d-flex">
                                        <v-switch
                                            v-model="user.status"
                                            :error-messages="errorMessages.status"
                                            :label="
                                                user.status === 'active'
                                                    ? t('users::status.active')
                                                    : t('users::status.not_active')
                                            "
                                            class="w-50"
                                            false-value="blocked"
                                            true-value="active"
                                        >
                                        </v-switch>
                                        <v-switch
                                            v-show="canResetPasswordFromRole"
                                            v-model="user.can_reset_password"
                                            :error-messages="errorMessages.can_reset_password"
                                            :false-value="0"
                                            :label="t('users::can-reset')"
                                            :true-value="true"
                                            :value="canResetPasswordFromRole"
                                            class="w-50"
                                        >
                                        </v-switch>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cls="12" lg="6" class="py-0">
                                        <v-text-field
                                            v-model="user.name"
                                            :error-messages="errorMessages.name"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t('users::create.name')}`"
                                            :rules="[(v) => !!v || t('users::validation.field.required')]"
                                            dense
                                            rounded
                                            background-color="#fff"
                                            outlined
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cls="12" lg="6" class="py-0">
                                        <v-text-field
                                            v-model="user.username"
                                            :error-messages="errorMessages.username"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t('users::create.username')}`"
                                            :rules="[(v) => !!v || t('default::validation.field.required')]"
                                            dense
                                            background-color="#fff"
                                            outlined
                                            rounded
                                        >
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cls="12" lg="6" class="py-0">
                                        <v-text-field
                                            v-if="!id"
                                            v-model="user.password"
                                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                            :error-messages="errorMessages.password"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t('users::create.password')}`"
                                            :rules="[(v) => !!v || t('default::validation.field.required')]"
                                            :type="showPassword ? 'text' : 'password'"
                                            dense
                                            rounded
                                            background-color="#fff"
                                            outlined
                                            @click:append="showPassword = !showPassword"
                                        >
                                        </v-text-field>
                                    </v-col>
                                    <v-col cls="12" lg="6" class="py-0">
                                        <v-text-field
                                            v-if="!id"
                                            v-model="passwordConfirmed"
                                            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                            :error-messages="errorMessages.confirm_password"
                                            :label="`${$root.defaultMandatoryAsteriks} ${t(
                                                'users::create.confirm_password'
                                            )}`"
                                            :rules="[
                                                (v) => !!v || t('default::validation.field.required'),
                                                passwordConfirmationRule,
                                            ]"
                                            :type="showPassword ? 'text' : 'password'"
                                            dense
                                            background-color="#fff"
                                            outlined
                                            rounded
                                            @click:append="showPassword = !showPassword"
                                        >
                                        </v-text-field>
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="12">
                                <b-form-actions
                                    :id="id"
                                    :back-route="$root.route('users.index')"
                                    @discard="resetUser"
                                    @save="saveUser"
                                >
                                    <template #buttons>
                                        <v-btn
                                            v-if="id"
                                            :disabled="!hasEmail"
                                            rounded
                                            color="primary"
                                            class="ml-2"
                                            @click="sendRecoveryPasswordMail"
                                        >
                                            {{ t('users::button.send_reset_password_link') }}
                                        </v-btn>
                                    </template>
                                </b-form-actions>
                            </v-col>
                        </v-row>
                    </div>
                </v-tab-item>
                <v-tab-item>
                    <div class="pt-10 px-2">
                        <v-row>
                            <v-col cols="12" class="py-0">
                                <roles-selector
                                    v-model="user.roles"
                                    :error-messages="errorMessages.roles"
                                    :label="t('users::create.role')"
                                    :rules="[(v) => !!v || t('users::validation.role.required')]"
                                    outlined
                                    background-color="#fff"
                                    chips
                                    small-chips
                                    clearable
                                    @change="extractActiveRoutes"
                                >
                                </roles-selector>
                            </v-col>
                            <v-col v-if="routes" cols="12" class="pt-0">
                                <v-card flat>
                                    <v-expansion-panels v-if="routes">
                                        <v-expansion-panel
                                            v-for="(item, key) in routes"
                                            v-show="isRoleActiveGroup(key)"
                                            :key="key"
                                        >
                                            <v-expansion-panel-header>{{ key }}</v-expansion-panel-header>
                                            <v-expansion-panel-content>
                                                <v-row no-gutters>
                                                    <v-col
                                                        v-for="route in item"
                                                        v-show="isAllowedRoleRoute(route.id)"
                                                        :key="route.id"
                                                        cols="12"
                                                        lg="3"
                                                        md="6"
                                                        sm="12"
                                                    >
                                                        <v-checkbox
                                                            v-model="permissions"
                                                            :label="route.name"
                                                            :value="route.id"
                                                            color="primary"
                                                            dense
                                                            background-color="#fff"
                                                            hide-details
                                                            size="100%"
                                                            @change="changePermission($event, route.id)"
                                                        >
                                                        </v-checkbox>
                                                    </v-col>
                                                </v-row>
                                            </v-expansion-panel-content>
                                        </v-expansion-panel>
                                    </v-expansion-panels>
                                </v-card>
                            </v-col>
                            <v-col cols="12">
                                <b-form-actions
                                    :id="id"
                                    :back-route="$root.route('users.index')"
                                    @discard="resetUser"
                                    @save="saveUser"
                                >
                                    <template #buttons>
                                        <v-btn
                                            v-if="id"
                                            color="primary"
                                            class="ml-2"
                                            :disabled="!hasEmail"
                                            @click="sendRecoveryPasswordMail"
                                        >
                                            <v-icon left small>mdi-account-key</v-icon>
                                            {{ t('users::button.send_reset_password_link') }}
                                        </v-btn>
                                    </template>
                                </b-form-actions>
                            </v-col>
                        </v-row>
                    </div>
                </v-tab-item>
                <slot name="additional-inputs" :user="user"></slot>
            </v-tabs>
        </v-col>
    </v-form>
</template>
<script>
let formSubmitMixin = require(process.env.MIX_APP_ROOT +
    '/bgo/bgo-components/resources/js/mixins/FormSubmit.js').default;

export default {
    name: 'BaseUserForm',
    mixins: [formSubmitMixin],
    props: {
        id: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            tabKey: 0,
            roleActiveRoutesIds: [],
            rolesLoaded: false,
            panel: [],
            allowed: true,
            isFormValid: false,
            showPassword: false,
            usersLoaded: false,
            hasEmail: false,
            passwordConfirmed: null,
            avatar: null,
            routes: {},
            roles: null,

            user: {
                status: 'active',
                name: null,
                email: null,
                password: null,
                roles: [],
                disabled_permissions: [],
            },
            errorMessages: {
                email: [],
                username: [],
                name: [],
                password: [],
                roles: [],
                can_reset_password: [],
            },
            accessInfo: [],
        };
    },
    computed: {
        passwordConfirmationRule() {
            return () => this.user.password === this.passwordConfirmed || t('users::password_confirm.mismatch');
        },
        canResetPasswordFromRole() {
            let canReset = false;

            _.forEach(this.getSelectedRoles(), (role) => {
                if (typeof role === 'string' || role instanceof String) {
                    role = this.roles.find((obj) => obj.id === role);
                }

                if (role.reset_password) {
                    canReset = true;
                }
            });

            return canReset;
        },
        permissions: {
            get() {
                return this.roleActiveRoutesIds.filter((routeId) => {
                    return !this.user.disabled_permissions.includes(routeId);
                });
            },
            set() {
                //nothing here
            },
        },
    },
    watch: {
        async avatar(newValue) {
            this.user.avatar = await this.toBase64(newValue);
        },
    },
    created() {
        this.can('api.v1.acl-routes.grouped') ? this.loadRoutes() : false;
    },
    methods: {
        loadUser() {
            if (_.isEmpty(this.id)) {
                this.loading = false;
                return;
            }

            axios
                .get(
                    route('api.v1.users.show', {
                        user: this.id,
                        fields: 'id,name,username,email,status,roles.id,avatar,disabled_permissions,can_reset_password',
                    })
                )
                .then((response) => {
                    this.user = response.data.data;
                    this.user.roles = _.map(this.user.roles, 'id')[0];
                    this.hasEmail = !!this.user.email; // casting to boolean
                    this.extractActiveRoutes();
                })
                .catch((error) => {
                    this.$root.notify('error', error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getSelectedRoles() {
            if (!this.user.roles) {
                return [];
            }

            if (Array.isArray(this.user.roles)) {
                return this.user.roles;
            }

            return [this.user.roles];
        },
        resetUser() {
            this.loadUser();
        },
        async saveUser() {
            //todo - post multipart/form-data @see FormSubmit.js
            if (this.avatar) {
                this.user.avatar_base64_name = _.get(this.avatar, 'name');
                this.user.avatar_base64 = await this.toBase64(this.avatar);
            } else if (!this.user.avatar) {
                this.user.avatar_base64 = null;
                this.user.avatar_base64_name = null;
            }

            this.user.roles = this.getSelectedRoles();

            this.submit(
                !_.isUndefined(this.user.id)
                    ? route('api.v1.users.update', { user: this.user.id })
                    : route('api.v1.users.store'),
                this.user,
                !_.isUndefined(this.user.id) ? 'put' : 'post',
                () => {
                    this.$root.notify('info', t('default::notification.saved'));
                    this.$root.goTo(route('users.index'));
                    this.user.roles = this.getSelectedRoles()[0];
                },
                () => {
                    this.user.roles = this.getSelectedRoles()[0];
                }
            );
        },
        removeAvatar: function () {
            this.avatar = null;
            this.user.avatar = null;
            this.user.avatar_base64 = null;
        },
        toBase64(file) {
            if (!file) {
                return null;
            }

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = (error) => reject(error);
            });
        },
        sendRecoveryPasswordMail(redirect = false) {
            if (this.hasEmail) {
                axios
                    .post(route('api.v1.auth.reset-password'), {
                        email: this.user.email,
                        is_new: true,
                    })
                    .then(() => {
                        this.$root.notify('info', t('login::password.link.sent.message'));

                        if (redirect) {
                            this.$root.goTo(route('users.index'));
                        }
                    })
                    .catch((error) => {
                        this.$root.parseErrorsAndNotify(error);
                    });
            } else {
                this.$root.notify('error', t('login::validation.email.required'));
            }
        },
        loadRoutes() {
            axios
                .get(route('api.v1.acl-routes.grouped'), {
                    params: {
                        allowed_for_guests: 0,
                        allowed_for_all: 0,
                        limit: -1,
                    },
                })
                .then((response) => {
                    this.routes = response.data.data;
                    this.loadRoles();
                });
        },
        loadRoles() {
            axios
                .get(
                    route('api.v1.roles.index', {
                        fields: 'id,name,acl_routes,description,reset_password',
                        limit: -1,
                    })
                )
                .then((response) => {
                    this.roles = response.data.data;
                    this.rolesLoaded = true;
                    this.loadUser();
                });
        },
        extractActiveRoutes() {
            this.roleActiveRoutesIds = [];

            _.forEach(this.roles, (role) => {
                if (this.getSelectedRoles().includes(role.id)) {
                    this.roleActiveRoutesIds = _.concat(
                        this.roleActiveRoutesIds,
                        role.acl_routes.map((route) => route.id)
                    );
                }
            });

            this.roleActiveRoutesIds = _.uniq(this.roleActiveRoutesIds);
        },
        isAllowedRoleRoute(routeId) {
            return this.roleActiveRoutesIds.includes(routeId);
        },
        isRoleActiveGroup(group) {
            return (
                _.filter(this.routes[group], (route) => {
                    return this.roleActiveRoutesIds.includes(route.id);
                }).length > 1
            );
        },
        changePermission(all, routeId) {
            if (all.includes(routeId)) {
                _.remove(this.user.disabled_permissions, (permissionId) => {
                    return permissionId === routeId;
                });
            } else {
                this.user.disabled_permissions.push(routeId);
            }
        },
    },
};
</script>
